import React, { useEffect, useState, useCallback } from 'react';
import css from './PickupDelivery.module.scss';
import '../../css/themes.scss';
import AddressForm from '../AddressForm/AddressForm';
import { IView } from 'Framework';
import Footer from '../Footer/Footer';
import { ComponentMapIds } from '../map';
import { Button, Checkbox, ScrollBar, TextField } from '@framework-controls/core';
import { strings } from 'src/localization';
import MediaQuery from 'react-responsive';
import { ApptPULoc, ApptDOLoc, ApptLocCom, ZIPSearchReturn, VehicleRec, ascFormEngagementInput, ascCtaInteractionInput, ascPageviewInput, ascMenuInteractionInput, ascElementConfigurationInput } from 'src/models';
import { ascFormEngagement, ascCtaInteraction, getElementColor, ascPageview, ascMenuInteraction, ascElementConfiguration } from 'src/helpers/EventHelper/EventHelper';

export interface IProps {
    name?: string;
    nameID?: string;
    puAddress: string;
    puAddress2: string,
    puZipPostal: string;
    puCity: string;
    puStateProvince: string;
    doAddress: string;
    doAddress2: string,
    doZipPostal: string;
    doCity: string;
    doStateProvince: string;
    pudoAddComments: string;
    locale: string;
    zipReturn: ZIPSearchReturn;
    pushToMainPanes: (inputView: IView) => void;
    addLogEntry: (action: string, screen: string, details: string) => void;
    updatePickupInfo:(inp:ApptPULoc) => void;
    updateDropoffInfo:(inp:ApptDOLoc) => void;
    updateAddCom:(inp:ApptLocCom) => void;
    zipSearch: (zipCode:string) => void;
    clearZipSearch: () => void;
    vehicles: VehicleRec[];
    selectedVehicle?: string;
    theme?: string;
    countryCode: string;
}

const PickupDelivery = (props : IProps) => {
    const {name, nameID, puAddress, puAddress2, puZipPostal, puCity, puStateProvince, doAddress, doAddress2,
        doZipPostal, doCity, doStateProvince, locale, zipReturn} = props;
    const [sameAsCheckBox, setSameAsCheckBox] = useState<boolean>(true);
    const header = nameID ? name + ',' : strings.PULHeaderNoName;
    const [comments, setComments] = useState<string>('');
    const [charRemain, setCharRemain] = useState<number>(150);
    const [proceedDisabled, setProceedDisabled] = useState<boolean>(true);
    const [processBegin, setProcessBegin] = useState<boolean>(false);
    const [puProcess, setPUProcess] = useState<boolean>(false);
    const [doProcess, setDOProcess] = useState<boolean>(false);
    const [puProceedFlag, setPUProceedFlag] = useState<boolean>(false);
    const [doProceedFlag, setDOProceedFlag] = useState<boolean>(false);

    const addressBlurHandler = useCallback((elementText: string, formName: string) => {
        const ascFormEngagementInp: ascFormEngagementInput = {
            pageType: 'service',
            commType: 'form',
            commStatus: 'engage',
            elementText: elementText,
            department: 'service',
            formName: formName,
            formType: 'service_appointment',
        };
        ascFormEngagement(ascFormEngagementInp, props.vehicles, props.selectedVehicle);
    }, [props.vehicles, props.selectedVehicle]);

    const dropdownLogHandler = useCallback((elementText: string, eventAction: string) => {
        const ascMenuInteractionInp : ascMenuInteractionInput = {
            pageType: 'service',
            elementText: elementText,
            elementSubtype: 'dropdown',
            elementType: 'menu',
            eventAction: eventAction,
        };
        ascMenuInteraction(ascMenuInteractionInp);
    }, []);

    const pickupAddress = <AddressForm
        type='Pickup'
        sameAsCheckBox={sameAsCheckBox}
        puProcess={puProcess}
        setPUProcess={setPUProcess}
        address= {puAddress ? puAddress : ''}
        address2={puAddress2 ? puAddress2 : ''}
        zipPostal={puZipPostal ? puZipPostal : ''}
        cityTown={puCity ? puCity : ''}
        stateProvince={puStateProvince ? puStateProvince : ''}
        locale={locale}
        updatePickupInfo={props.updatePickupInfo}
        updateDeliveryInfo={props.updateDropoffInfo}
        setPUProceedFlag = {setPUProceedFlag}
        zipReturn = {zipReturn}
        zipSearch = {props.zipSearch}
        clearZipSearch = {props.clearZipSearch}
        addressBlurHandler = {addressBlurHandler}
        dropdownLogHandler = {dropdownLogHandler}
        countryCode={props.countryCode}
    />;
    const deliveryAddress = <AddressForm
        type='Delivery'
        sameAsCheckBox={sameAsCheckBox}
        doProcess={doProcess}
        setDOProcess={setDOProcess}
        address= {doAddress ? doAddress : ''}
        address2={doAddress2 ? doAddress2 : ''}
        zipPostal={doZipPostal ? doZipPostal : ''}
        cityTown={doCity ? puCity : ''}
        stateProvince={doStateProvince ? doStateProvince : ''}
        locale={props.locale}
        updateDeliveryInfo={props.updateDropoffInfo}
        setDOProceedFlag = {setDOProceedFlag}
        zipReturn = {zipReturn}
        zipSearch = {props.zipSearch}
        clearZipSearch = {props.clearZipSearch}
        addressBlurHandler = {addressBlurHandler}
        dropdownLogHandler = {dropdownLogHandler}
        countryCode={props.countryCode}
    />;

    // Sets the Process flags to true when Proceed is clicked and move on to the Summary page
    const footerHandler = (e: any) => {
        setPUProcess(true);
        setDOProcess(true);
        setProcessBegin(true);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.Proceed,
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
        const ascFormEngagementInp: ascFormEngagementInput = {
            pageType: 'service',
            commType: 'form',
            commStatus: 'engage',
            elementText: strings.Proceed,
            department: 'service',
            formName: 'PickupDelivery',
            formType: 'service_appointment',
        };
        ascFormEngagement(ascFormEngagementInp, props.vehicles, props.selectedVehicle);
        props.pushToMainPanes({
            viewId: ComponentMapIds.Summary,
            component: ComponentMapIds.Summary,
        });
    };

    // Controls the "Same as pick up location" checkbox
    const onCheckedHandler = useCallback((e: any) => {
        e.preventDefault();
        setSameAsCheckBox(!sameAsCheckBox);
    }, [sameAsCheckBox]);

    const checkboxEventHandler = useCallback((e: any) => {
        const elementColor = getElementColor(e, 'fill');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.SameAsCheckBox,
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'form',
            elementSubType: 'checkbox',
            eventAction: 'click',
            eventActionResult: sameAsCheckBox ? 'uncheck' : 'check',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: strings.SameAsCheckBox,
            elementType: 'forms',
            elementColor: elementColor,
            eventAction: 'click',
            eventActionResult: sameAsCheckBox ? 'uncheck' : 'check',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, e);
    }, [sameAsCheckBox, props.vehicles, props.selectedVehicle]);

    let cbkClass = 'primaryCkbChecked';
    if (!sameAsCheckBox) {
        cbkClass = 'primaryCkb';
    }

    // Updates the characters remaining for the Additional Comments box
    const commentChangeHandler = useCallback((value) => {
        if (value.length < 151) {
            setComments(value);
            setCharRemain(150-value.length);
        }
    }, []);

    const onBlurHandler = useCallback(() => {
        const ascFormEngagementInp: ascFormEngagementInput = {
            pageType: 'contact',
            commType: 'form',
            commStatus: 'engage',
            elementText: strings.AdditionalComments,
            department: 'service',
            formName: 'PickUpDeliveryComments',
            formType: 'service_appointment',
        };
        ascFormEngagement(ascFormEngagementInp, props.vehicles, props.selectedVehicle);
    }, [props.vehicles, props.selectedVehicle]);

    // Log when entering the page
    useEffect(() => {
        props.addLogEntry('Screen Entry', 'Pickup and Delivery', '');
        const ascPageViewInp: ascPageviewInput = {
            pageType: 'service',
            errorCode: '',
            itemResults: '',
        };
        ascPageview(ascPageViewInp, props.vehicles, props.selectedVehicle);
    }, [props.addLogEntry, props.selectedVehicle, props.vehicles]);

    // Controls if the Proceed button is disabled or not
    useEffect(() => {
        if (sameAsCheckBox) {
            if (puProceedFlag) {
                setProceedDisabled(false);
            } else {
                setProceedDisabled(true);
            }
        } else {
            if (puProceedFlag && doProceedFlag) {
                setProceedDisabled(false);
            } else {
                setProceedDisabled(true);
            }
        }
    }, [puProceedFlag, doProceedFlag, sameAsCheckBox]);

    // Once state has been updated with the address(es), update state with the comments
    // reset the processBegin variable to prevent an infinite loop
    useEffect(() => {
        if (!puProcess && !doProcess && !proceedDisabled && processBegin) {
            const apptLocCom: ApptLocCom ={
                pudoAddComments: comments,
            };
            props.updateAddCom(apptLocCom);
            setProcessBegin(false);
        }
    }, [comments, doProcess, puProcess, props.updateAddCom, proceedDisabled, processBegin]);

    return (
        <div>
            <ScrollBar scrollVertical>
                <div className={css.pickupDelivery}>
                    <h2>{header}</h2>
                    <h3>{strings.PULSubHeaderMessage}</h3>
                    <hr className={css.topLine}/>
                    <div>
                        <div className={css.reqField}>{strings.RequiredFields} </div>
                        <hr className={css.topLineMobile}/>
                        <div className={css.formWrapper}>
                            <div className={css.puFormWrapper}>
                                <h2 className={css.PULHeader}>{strings.PickupLocation}</h2>
                                <MediaQuery minWidth={601}>
                                    <div className={css.checkBoxWrap}>
                                        <div className = {css.hidden}>
                                            <Checkbox
                                                id='sameAsPickUp'
                                                checked={sameAsCheckBox}
                                                size='rg'
                                                label={strings.SameAsCheckBox}
                                                labelPlacement='end'
                                                onChange={() => onCheckedHandler!}
                                            />
                                        </div>
                                    </div>
                                </MediaQuery>
                                <div className={css.body}>
                                    <div className={css.pickup}>
                                        {pickupAddress}
                                    </div>
                                </div>
                            </div>
                            <div className={css.doFormWrapper}>
                                <h2 className={css.DOLHeader}>{strings.DropOffLocation}</h2>
                                <div className={css.checkBoxWrap}>
                                    <div onClick={checkboxEventHandler}>
                                        <Button onClick={(e: any) => onCheckedHandler!(e)}>
                                            <Checkbox
                                                id='sameAsPickUp'
                                                checked={sameAsCheckBox}
                                                size='rg'
                                                label={strings.SameAsCheckBox}
                                                labelPlacement='end'
                                                variant = {cbkClass}
                                            />
                                        </Button>
                                    </div>
                                </div>
                                <div className={css.body}>
                                    <div className={css.delivery}>
                                        {deliveryAddress}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className={css.comments}>
                            <TextField
                                hint={strings.AdditionalComments}
                                type='text'
                                value={comments}
                                id='PickUpDeliveryComments'
                                size='full'
                                multiline
                                onChange={(_, value) => commentChangeHandler(value)}
                                onBlur={onBlurHandler}
                                rows={5}
                                disableUnderline
                            />
                        </div>
                        <div>
                            <h3 className={css.charRemain}>{charRemain.toString()}{strings.CharactersRemaining}</h3>
                        </div>
                    </div>
                </div>
            </ScrollBar>
            <Footer
                id='PickupDeliveryFooter'
                handler={footerHandler}
                message={strings.Proceed}
                secondaryhandler={footerHandler}
                isDisabled={proceedDisabled}
                theme={props.theme}
            />
        </div>
    );
};

export default PickupDelivery;